import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const FieldLabel = ({ children, idx }) => (
    <label className="flex items-end justify-between mb-2">
        <span className="text-sm font-medium text-ink">{children}</span>
        <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">
            {String(idx).padStart(2, "0")}
        </span>
    </label>
);

const HoneypotField = () => (
    <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
    />
);

export default function ReviewForm() {
    const [submittingReview, setSubmittingReview] = useState(false);

    const handleReviewSubmit = async (event) => {
        event.preventDefault();
        if (submittingReview) return;
        const form = event.currentTarget;
        const formData = new FormData(form);
        setSubmittingReview(true);
        try {
            const response = await fetch("/api/testimonials/submit", {
                method: "POST",
                body: formData,
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload.ok) {
                throw new Error(
                    payload.message ||
                        "The review could not be submitted. Please email support@reachforge.io.",
                );
            }
            toast.success("Review submitted for approval.", {
                description:
                    "Thank you. It will only appear publicly after manual review.",
            });
            form.reset();
        } catch (error) {
            toast.error("Could not submit this review", {
                description:
                    error instanceof Error
                        ? error.message
                        : "Please email support@reachforge.io directly.",
            });
        } finally {
            setSubmittingReview(false);
        }
    };

    return (
        <div
            className="grid lg:grid-cols-12 border border-border bg-surface"
            data-testid="testimonial-review-form-card"
        >
            <div className="lg:col-span-5 p-6 md:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-border">
                <div className="flex items-center justify-between gap-4 mb-10">
                    <Link
                        to="/"
                        data-testid="reviews-back-home"
                        className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-ink link-underline"
                    >
                        <ArrowLeft size={14} /> Back
                    </Link>
                </div>
                <span className="overline">Leave a review</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-4 text-ink">
                    Used ReachForge Leads? Share practical feedback.
                </h2>
                <p className="mt-4 text-ink-soft">
                    Reviews are checked before publication. Your email is used
                    only to verify the submission and is not shown on the public
                    website.
                </p>
            </div>
            <form
                data-testid="testimonial-review-form"
                onSubmit={handleReviewSubmit}
                className="lg:col-span-7 p-6 md:p-8 lg:p-10 grid gap-5 bg-bg"
            >
                <HoneypotField />
                <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                        <FieldLabel idx={1}>Full name</FieldLabel>
                        <input
                            data-testid="review-input-name"
                            name="customer_name"
                            required
                            type="text"
                            maxLength={120}
                            className="input-sharp"
                            placeholder="Jane Smith"
                        />
                    </div>
                    <div>
                        <FieldLabel idx={2}>Work email</FieldLabel>
                        <input
                            data-testid="review-input-email"
                            name="customer_email"
                            required
                            type="email"
                            maxLength={160}
                            className="input-sharp"
                            placeholder="jane@company.com"
                        />
                    </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-5">
                    <div>
                        <FieldLabel idx={3}>Role</FieldLabel>
                        <input
                            data-testid="review-input-role"
                            name="customer_role"
                            type="text"
                            maxLength={120}
                            className="input-sharp"
                            placeholder="Sales manager"
                        />
                    </div>
                    <div>
                        <FieldLabel idx={4}>Company</FieldLabel>
                        <input
                            data-testid="review-input-company"
                            name="company_name"
                            type="text"
                            maxLength={120}
                            className="input-sharp"
                            placeholder="Acme Ltd."
                        />
                    </div>
                    <div>
                        <FieldLabel idx={5}>Short tag</FieldLabel>
                        <input
                            data-testid="review-input-tag"
                            name="tag"
                            type="text"
                            maxLength={80}
                            className="input-sharp"
                            placeholder="Manufacturing"
                        />
                    </div>
                </div>
                <div>
                    <FieldLabel idx={6}>Review</FieldLabel>
                    <textarea
                        data-testid="review-input-quote"
                        name="quote"
                        required
                        maxLength={600}
                        className="input-sharp"
                        rows={5}
                        placeholder="What did ReachForge Leads help you do?"
                    />
                </div>
                <label className="flex items-start gap-3 text-sm text-ink-soft">
                    <input
                        data-testid="review-input-consent"
                        name="consent"
                        value="1"
                        type="checkbox"
                        required
                        className="mt-1"
                    />
                    <span>
                        I confirm this review can be reviewed and, if approved,
                        displayed publicly on reachforge.io.
                    </span>
                </label>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute">
                        Protected by rate limits and bot filtering.
                    </p>
                    <button
                        type="submit"
                        data-testid="review-submit"
                        className="btn btn-primary"
                        disabled={submittingReview}
                    >
                        {submittingReview ? "Submitting..." : "Submit review"}{" "}
                        <ArrowUpRight size={14} />
                    </button>
                </div>
            </form>
        </div>
    );
}
